import { useState } from 'react';
import { useTracker } from '../../state/store';
import { audioEngine } from '../../engine/audio';
import { loadModule } from '../../engine/loaders';
import { Button, Panel, TextInput } from './primitives';

/**
 * Browse and load modules from The Mod Archive.
 *
 * Requests go through a same-origin reverse proxy (`/ma/*`) because
 * modarchive.org sends no CORS headers. Searches only ever fire on an explicit
 * user action — this is a browser, not a crawler.
 */

export interface ArchiveResult {
  id: number;
  filename: string;
  title: string;
  format: string;
  /** Whether FinTracker can actually play it. */
  supported: boolean;
}

/** The archive hosts a dozen formats; these are the two this tracker reads. */
const SUPPORTED_FORMATS = new Set(['MOD', 'XM']);

function decodeEntities(text: string): string {
  return text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .trim();
}

function formatFromFilename(filename: string): string {
  const ext = filename.split('.').pop();
  return ext ? ext.toUpperCase() : '';
}

/**
 * Pull results out of the search page's HTML.
 *
 * Anchored on the download links, which carry both the module id and its
 * filename — a far steadier landmark than any particular table markup. The
 * song title and format icon are then read from the same row, and everything
 * degrades to the filename if the page layout shifts under us.
 */
export function parseSearchResults(html: string): ArchiveResult[] {
  const results: ArchiveResult[] = [];
  const seen = new Set<number>();

  const chunks = html.split('downloads.php?moduleid=');
  for (let i = 1; i < chunks.length; i++) {
    const chunk = chunks[i];
    const head = /^(\d+)#([^"'\s&]*)/.exec(chunk);
    if (!head) continue;

    const id = Number(head[1]);
    if (seen.has(id)) continue;
    seen.add(id);

    let filename = head[2];
    try {
      filename = decodeURIComponent(filename);
    } catch {
      // A badly-encoded filename is still a usable label; keep it as-is.
    }

    // Both the format icon and the title sit within the same table row, which
    // is comfortably inside the next couple of kilobytes.
    const row = chunk.slice(0, 2000);
    const formatMatch = /<span class="format-icon">([A-Za-z0-9]+)<\/span>/i.exec(row);
    const titleMatch = /view_by_moduleid&(?:amp;)?query=\d+"\s+title="([^"]*)"/i.exec(row);

    const format = (formatMatch?.[1] ?? formatFromFilename(filename)).toUpperCase();
    const title = titleMatch ? decodeEntities(titleMatch[1]) : '';

    results.push({
      id,
      filename,
      title: title || filename,
      format,
      supported: SUPPORTED_FORMATS.has(format),
    });
  }

  return results;
}

export function ModArchive() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ArchiveResult[]>([]);
  const [status, setStatus] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const loadSong = useTracker((s) => s.loadSong);

  const search = async () => {
    const term = query.trim();
    if (!term) return;
    setStatus('searching…');
    setResults([]);
    try {
      const params = new URLSearchParams({
        request: 'search',
        submit: 'Find',
        search_type: 'filename_or_songtitle',
        query: term,
      });
      const response = await fetch(`/ma/search?${params}`);
      if (!response.ok) throw new Error(`archive returned ${response.status}`);
      const html = await response.text();
      const found = parseSearchResults(html);
      setResults(found);
      setStatus(found.length ? `${found.length} result${found.length === 1 ? '' : 's'}` : 'nothing found');
    } catch (error) {
      setStatus(
        error instanceof Error
          ? `search failed: ${error.message}`
          : 'search failed — the archive proxy may be unreachable',
      );
    }
  };

  const load = async (result: ArchiveResult) => {
    setLoadingId(result.id);
    setStatus(`downloading ${result.filename}…`);
    try {
      const response = await fetch(`/ma/download?moduleid=${result.id}`);
      if (!response.ok) throw new Error(`download returned ${response.status}`);
      const buffer = await response.arrayBuffer();
      const { song, warnings } = loadModule(buffer);
      await audioEngine.init();
      loadSong(song, result.filename, warnings);
      setStatus(`loaded "${song.name || result.title}" · ${song.channels}ch ${song.format.toUpperCase()}`);
    } catch (error) {
      setStatus(error instanceof Error ? `failed: ${error.message}` : 'failed to load');
    } finally {
      setLoadingId(null);
    }
  };

  const loadById = async () => {
    const id = Number(query.trim().match(/(\d{2,})/)?.[1]);
    if (!Number.isFinite(id) || id <= 0) {
      setStatus('enter a numeric module id, or a modarchive.org URL containing one');
      return;
    }
    // Loading by id skips the search page, so the format is unknown until the
    // bytes arrive — the loader sniffs it and reports anything it cannot read.
    await load({
      id,
      filename: `module-${id}`,
      title: `module ${id}`,
      format: '',
      supported: true,
    });
  };

  return (
    <Panel
      title="Mod Archive"
      actions={
        <a
          href="https://modarchive.org"
          target="_blank"
          rel="noreferrer noopener"
          className="font-mono text-[10px] text-ink-500 hover:text-acid-400"
        >
          modarchive.org ↗
        </a>
      }
    >
      <div className="flex flex-col gap-2">
        <div className="flex gap-1">
          <TextInput
            value={query}
            onChange={setQuery}
            placeholder="title, filename, or module id"
            className="min-w-0 flex-1"
            onKeyDown={(e) => {
              // The pattern editor listens on window, so keep keys in the field.
              e.stopPropagation();
              if (e.key === 'Enter') void search();
            }}
          />
          <Button onClick={() => void search()} tone="accent">
            Find
          </Button>
        </div>
        <Button onClick={() => void loadById()} title="Load a module directly by its archive id">
          Load by ID / URL
        </Button>

        {status && <p className="font-mono text-[10px] text-acid-400">{status}</p>}

        {results.length > 0 && (
          <ul className="max-h-72 space-y-0.5 overflow-y-auto">
            {results.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => void load(r)}
                  disabled={loadingId !== null || !r.supported}
                  title={
                    r.supported
                      ? `Load ${r.filename}`
                      : `${r.format} is not a format FinTracker can read — it plays MOD and XM.`
                  }
                  className="flex w-full items-start gap-2 rounded px-2 py-1 text-left transition-colors hover:bg-ink-800 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                >
                  <span
                    className={`mt-[2px] shrink-0 rounded px-1 py-px font-mono text-[9px] ${
                      r.supported ? 'bg-acid-900 text-acid-300' : 'bg-ink-800 text-ink-500'
                    }`}
                  >
                    {r.format || '??'}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-mono text-[11px] text-white">{r.title}</span>
                    <span className="block truncate font-mono text-[10px] text-ink-500">
                      {r.filename}
                      {loadingId === r.id && ' · loading…'}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <p className="font-mono text-[10px] leading-relaxed text-ink-500">
          Modules are hosted by The Mod Archive and remain the work of their original authors.
        </p>
      </div>
    </Panel>
  );
}
